"use client";

import Image from "next/image";
import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";
import { FrameIcon, PhoneIcon } from "./booking-icons";

type HelpItem = {
  title: string;
  value: string;
};

/** Matches GradientBorderLink's brand sweep, reused on the outline CTA. */
const CTA_GRADIENT_BORDER = {
  background:
    "linear-gradient(var(--background), var(--background)) padding-box, " +
    "linear-gradient(269.48deg, #1BC4F9 3.26%, #5A6BE0 65.08%, #DC41CA 135.82%) border-box",
};

export default function BookingHelp() {
  const t = useTranslations("Booking");
  const locale = useLocale();
  const isArabic = locale === "ar";
  const items = t.raw("helpItems") as HelpItem[];

  return (
    <section
      dir={isArabic ? "rtl" : "ltr"}
      className="relative px-4 py-8 md:px-6 lg:px-8 lg:py-12"
    >
      <div
        className="relative mx-auto flex w-full max-w-310 flex-col items-center gap-8 overflow-hidden rounded-3xl border border-white/8 px-6 py-8 sm:px-10 lg:flex-row lg:items-center lg:justify-between lg:gap-10 lg:px-10 lg:py-7"
        style={{
          backgroundImage:
            "linear-gradient(197.24deg, rgba(4, 209, 241, 0.06) 23.56%, rgba(124, 81, 189, 0.06) 89.91%, rgba(186, 36, 149, 0.06) 268.11%)",
        }}
      >
        <div
          aria-hidden="true"
          className={`pointer-events-none absolute top-1/2 hidden size-100 -translate-y-1/2 rounded-full opacity-40 blur-[160px] lg:block ${
            isArabic ? "-start-30" : "-end-30"
          }`}
          style={{
            background:
              "linear-gradient(29.56deg, rgba(4, 209, 241, 0.4) 1.15%, rgba(124, 81, 189, 0.4) 58.15%, rgba(186, 36, 149, 0.4) 108.11%)",
          }}
        />

        {/* Heading, blurb and CTA form one column; the three facts sit beside
            it on wide screens rather than stacking underneath. */}
        <div className="relative z-10 flex w-full min-w-0 flex-col items-start gap-4 text-start lg:w-auto lg:shrink-0">
          <div className="flex flex-col items-start gap-2">
            <h2 className="m-0 text-[22px] font-black leading-[1.5] text-white sm:text-[24px]">
              {t("helpTitle")}
            </h2>
            <p className="m-0 text-[14px] leading-[1.8] text-white/56">
              {t("helpDescription")}
            </p>
          </div>

          <Link
            href={`/${locale}#contact`}
            style={CTA_GRADIENT_BORDER}
            className="inline-flex min-h-10 items-center justify-center gap-2.5 rounded-full border border-transparent px-8 text-[15px] font-medium text-white transition-opacity duration-300 hover:opacity-85"
          >
            <PhoneIcon className="size-4.5" />
            <span>{t("helpCta")}</span>
          </Link>
        </div>

        <ul className="relative z-10 m-0 flex w-full list-none flex-col gap-5 p-0 lg:w-auto lg:flex-row lg:items-center lg:gap-18">
          {items.map((item) => (
            <li key={item.title} className="flex items-center gap-4">
              <FrameIcon className="size-8 shrink-0" />
              <span className="flex min-w-0 flex-col gap-1">
                <span className="text-[16px] font-bold text-white">
                  {item.title}
                </span>
                <span className="whitespace-nowrap text-[14px] text-white/56">
                  {item.value}
                </span>
              </span>
            </li>
          ))}
        </ul>

        {/* Height-capped so the portrait render can't stretch the banner past
            the short, wide card the design calls for. */}
        <div className="relative z-10 hidden h-38 w-56 shrink-0 lg:block">
          <span
            aria-hidden="true"
            className="absolute left-1/2 top-1/2 size-38 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(4,209,241,0.28)_0%,rgba(4,209,241,0)_70%)] blur-xl"
          />
          <Image
            src="/assets/images/sabah/robot-cta.png"
            alt={t("helpImageAlt")}
            width={330}
            height={451}
            className="relative h-full w-full object-contain"
          />
        </div>
      </div>
    </section>
  );
}
